import React, { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Collapse,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { ColumnConfig } from '@shared/hooks/useTableColumns';

interface ColumnSettingsModalProps {
  open: boolean;
  onClose: () => void;
  columns: ColumnConfig[];
  onToggleVisibility: (field: string) => void;
  onMoveColumn: (field: string, direction: 'up' | 'down') => void;
  onReset: () => void;
  getColumnLabel?: (field: string) => string;
  onWidthChange?: (field: string, width: number) => void;
  getColumnWidth?: (field: string) => number | undefined;
  onColumnsReorder?: (columns: ColumnConfig[]) => void;
}

const ColumnSettingsModal: FC<ColumnSettingsModalProps> = ({
  open,
  onClose,
  columns,
  onToggleVisibility,
  onMoveColumn,
  onReset,
  getColumnLabel,
  onWidthChange,
  getColumnWidth,
  onColumnsReorder,
}) => {
  const sortedColumns = [...columns].sort((a, b) => a.order - b.order);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [widthInputs, setWidthInputs] = useState<Record<string, number>>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleToggleExpand = (field: string) => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(field)) {
      newExpanded.delete(field);
    } else {
      newExpanded.add(field);
      // Инициализируем значение ширины при раскрытии
      if (!widthInputs[field] && getColumnWidth) {
        setWidthInputs(prev => ({
          ...prev,
          [field]: getColumnWidth(field) || columns.find(c => c.field === field)?.width || 150
        }));
      }
    }
    setExpandedFields(newExpanded);
  };

  const handleWidthChange = (field: string, value: number) => {
    setWidthInputs(prev => ({ ...prev, [field]: value }));
    if (onWidthChange) {
      onWidthChange(field, value);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newColumns = [...sortedColumns];
    const draggedItem = newColumns[draggedIndex];
    newColumns.splice(draggedIndex, 1);
    newColumns.splice(dropIndex, 0, draggedItem);

    // Обновляем порядок
    const updated = newColumns.map((col, idx) => ({ ...col, order: idx }));
    
    // Используем onColumnsReorder если доступен, иначе используем пошаговое перемещение
    if (onColumnsReorder) {
      onColumnsReorder(updated);
    } else {
      // Fallback: перемещаем пошагово
      const direction = draggedIndex < dropIndex ? 'down' : 'up';
      const steps = Math.abs(draggedIndex - dropIndex);
      
      let currentIndex = draggedIndex;
      for (let i = 0; i < steps; i++) {
        const targetField = sortedColumns[currentIndex].field;
        onMoveColumn(targetField, direction);
        currentIndex += direction === 'down' ? 1 : -1;
      }
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Настройка колонок таблицы</Typography>
          <Button size="small" onClick={onReset} color="secondary">
            Сбросить
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <List>
          {sortedColumns.map((column, index) => (
            <React.Fragment key={column.field}>
              <ListItem
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  mb: 1,
                  backgroundColor: column.visible ? 'white' : '#f5f5f5',
                  cursor: 'move',
                  opacity: draggedIndex === index ? 0.5 : 1,
                  borderColor: dragOverIndex === index ? '#1976d2' : '#e0e0e0',
                  borderWidth: dragOverIndex === index ? 2 : 1,
                  transition: 'all 0.2s ease',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={column.visible}
                      onChange={() => onToggleVisibility(column.field)}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        textDecoration: column.visible ? 'none' : 'line-through',
                        opacity: column.visible ? 1 : 0.6,
                      }}
                    >
                      {getColumnLabel ? getColumnLabel(column.field) : column.field}
                    </Typography>
                  }
                  sx={{ flex: 1 }}
                />
                <ListItemSecondaryAction>
                  <Box display="flex" gap={0.5} alignItems="center">
                    {onWidthChange && (
                      <IconButton
                        size="small"
                        onClick={() => handleToggleExpand(column.field)}
                        title={expandedFields.has(column.field) ? "Скрыть настройки ширины" : "Настроить ширину"}
                      >
                        {expandedFields.has(column.field) ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => onMoveColumn(column.field, 'up')}
                      disabled={index === 0}
                      title="Переместить вверх"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onMoveColumn(column.field, 'down')}
                      disabled={index === sortedColumns.length - 1}
                      title="Переместить вниз"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
              {onWidthChange && (
                <Collapse in={expandedFields.has(column.field)} timeout="auto" unmountOnExit>
                  <Box sx={{ pl: 4, pr: 4, pb: 2 }}>
                    <TextField
                      type="number"
                      label="Ширина колонки (px)"
                      value={widthInputs[column.field] ?? (getColumnWidth ? getColumnWidth(column.field) : column.width) ?? 150}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        if (!isNaN(value) && value > 0) {
                          handleWidthChange(column.field, value);
                        }
                      }}
                      size="small"
                      fullWidth
                      inputProps={{ min: 50, max: 1000 }}
                      helperText="Минимум: 50px, Максимум: 1000px"
                    />
                  </Box>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Готово
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnSettingsModal;

